import React from 'react';
import { Box, ButtonBase, Typography } from '@mui/material';
import { displayText, Severity, severityOf } from '../helpers';
import { launchColors } from '../launchTheme';
import { ActivityProjection } from '../types';
import DepartmentBadge from './DepartmentBadge';
import DueLabel from './DueLabel';
import PhaseChip from './PhaseChip';
import { StatusPill, WorkStatusPill } from './StatusPill';

export const severityBorder: Record<Severity, string> = {
  blocked: launchColors.dangerText,
  late: launchColors.dangerText,
  risk: launchColors.plum,
  soon: launchColors.warningMarker,
  missing: launchColors.textMuted,
  ok: 'transparent',
  done: launchColors.moss,
};

interface Props {
  activity: ActivityProjection;
  asOf: string;
  launchName?: string;
  compact?: boolean;
  action?: React.ReactNode;
  onOpen: () => void;
}

const ActivityRow: React.FC<Props> = ({ activity, asOf, launchName, compact = false, action, onOpen }) => {
  const severity = severityOf(activity);
  const title = displayText(activity.title);
  const marker = activity.kind === 'calendar_marker';
  return (
    <Box
      role="listitem"
      onClick={onOpen}
      sx={{
        display: 'grid',
        gridTemplateColumns: compact
          ? 'minmax(0, 1fr) auto'
          : {
            xs: 'minmax(0, 1fr)',
            md: `minmax(0, 2.4fr) minmax(0, 1.3fr) 170px 150px${action ? ' auto' : ''}`,
          },
        columnGap: 1.5,
        rowGap: 1,
        alignItems: 'center',
        px: 1.75,
        py: 1.25,
        borderLeft: `4px solid ${severityBorder[severity]}`,
        borderBottom: `1px solid ${launchColors.divider}`,
        bgcolor: activity.included ? launchColors.card : launchColors.section,
        cursor: 'pointer',
        '&:last-of-type': { borderBottom: 'none' },
        '&:hover': { bgcolor: launchColors.section },
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <ButtonBase
          onClick={(event) => { event.stopPropagation(); onOpen(); }}
          sx={{
            display: 'block',
            width: '100%',
            textAlign: 'left',
            borderRadius: 1,
            '&.Mui-focusVisible': { outline: `2px solid ${launchColors.navy}`, outlineOffset: 2 },
          }}
        >
          <Typography component="span" noWrap title={title} sx={{ display: 'block', fontSize: 14, fontWeight: 600, color: launchColors.text }}>{title}</Typography>
        </ButtonBase>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.25, minWidth: 0, flexWrap: 'wrap' }}>
          {launchName && <Typography component="span" noWrap sx={{ fontSize: 12, fontWeight: 600, color: launchColors.navy }}>{launchName}</Typography>}
          <PhaseChip phaseKey={activity.phaseKey} name={activity.phaseName} />
          {marker && <StatusPill label="Hållpunkt" fg={launchColors.petrolDark} bg={launchColors.petrolLight} />}
          {!activity.included && <StatusPill label="Utanför omfattning" fg={launchColors.textSecondary} bg={launchColors.neutralBg} />}
          {activity.activeBlocker && <StatusPill label="Blockerad" fg={launchColors.dangerText} bg={launchColors.dangerBg} />}
          {compact && <DepartmentBadge name={activity.roleText} size={18} nameSize={12} />}
        </Box>
      </Box>
      {!compact && <DepartmentBadge name={activity.roleText} />}
      {!compact && <Box><WorkStatusPill status={activity.workStatus} /></Box>}
      <DueLabel due={activity.due} asOf={asOf} status={activity.scheduleStatus} align={compact ? 'right' : 'left'} />
      {action && !compact && <Box onClick={(event) => event.stopPropagation()}>{action}</Box>}
    </Box>
  );
};

export default ActivityRow;
